import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Project } from '@/utils/pocketbase';

export interface DeckWizardResult {
  name: string;
  projectId?: string;
  brief: {
    team?: string;
    tractionNotes?: string;
    ask?: string;
  };
}

interface DeckWizardProps {
  projects: Project[];
  isSubmitting: boolean;
  onSubmit: (result: DeckWizardResult) => void;
  onCancel: () => void;
}

const NO_PROJECT = '__none__';

/**
 * Create flow for an investor deck.
 *
 * Deliberately shorter than the landing wizard: eleven of the fourteen slides are
 * built from artifacts the project already holds, so asking about the product again
 * would only give the model a second, worse version of what it already reads. The
 * brief step collects the three things no artifact carries — who the team is, what
 * traction exists, and what is being asked of the investor.
 *
 * Nothing on the second step is required. An unanswered question becomes a hidden
 * placeholder slide rather than an invented one (spec decision #8), which is the
 * honest outcome: made-up traction in a pitch is a reputational risk, not a nicety.
 */
export function DeckWizard({ projects, isSubmitting, onSubmit, onCancel }: DeckWizardProps) {
  const { t } = useTranslation('builder');
  const [step, setStep] = useState<0 | 1>(0);

  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState<string>(NO_PROJECT);
  const [team, setTeam] = useState('');
  const [traction, setTraction] = useState('');
  const [ask, setAsk] = useState('');

  const canContinue = name.trim().length > 0;

  const submit = () => {
    if (!canContinue || isSubmitting) return;
    onSubmit({
      name: name.trim(),
      projectId: projectId === NO_PROJECT ? undefined : projectId,
      brief: {
        team: team.trim() || undefined,
        tractionNotes: traction.trim() || undefined,
        ask: ask.trim() || undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-wrap items-center gap-[8px]">
        {([t('deck.wizard.steps.about'), t('deck.wizard.steps.brief')] as const).map((label, index) => (
          <div key={label} className="flex items-center gap-[8px]">
            <span
              className={
                'inline-flex h-[28px] min-w-[28px] items-center justify-center rounded-full px-[8px] font-manrope text-[13px] font-bold ' +
                (index <= step
                  ? 'bg-[var(--main-brand,#2197f1)] text-white'
                  : 'bg-[var(--grey-accent,#eeeeee)] text-[color:var(--grey-text,#969696)]')
              }
            >
              {index + 1}
            </span>
            <span
              className={
                'font-manrope text-[14px] font-semibold ' +
                (index <= step
                  ? 'text-[color:var(--black-text,#0d0d0d)]'
                  : 'text-[color:var(--grey-text,#969696)]')
              }
            >
              {label}
            </span>
            {index === 0 && <span className="mx-[4px] h-px w-[24px] bg-[var(--grey-accent,#eeeeee)]" />}
          </div>
        ))}
      </div>

      {step === 0 ? (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="deck-name" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.name.label')}
            </Label>
            <Input
              id="deck-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('deck.wizard.name.placeholder')}
              maxLength={120}
              autoFocus
              className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.name.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="deck-project" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.project.label')}
            </Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger id="deck-project">
                <SelectValue placeholder={t('deck.wizard.project.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>{t('deck.wizard.project.placeholder')}</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.project.hint')}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="deck-team" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.team.label')}
            </Label>
            <Textarea
              id="deck-team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              placeholder={t('deck.wizard.team.placeholder')}
              rows={3}
              maxLength={800}
              autoFocus
              className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.team.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="deck-traction" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.traction.label')}
            </Label>
            <Textarea
              id="deck-traction"
              value={traction}
              onChange={(e) => setTraction(e.target.value)}
              placeholder={t('deck.wizard.traction.placeholder')}
              rows={3}
              maxLength={800}
              className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.traction.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="deck-ask" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.ask.label')}
            </Label>
            <Textarea
              id="deck-ask"
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              placeholder={t('deck.wizard.ask.placeholder')}
              rows={3}
              maxLength={800}
              className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('deck.wizard.ask.hint')}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-[8px]">
        <Button
          variant="brand-outline"
          size="brand"
          onClick={step === 0 ? onCancel : () => setStep(0)}
          disabled={isSubmitting}
        >
          {step === 0 ? t('deck.wizard.cancel') : t('deck.wizard.back')}
        </Button>

        {step === 0 ? (
          <Button variant="brand" size="brand" onClick={() => setStep(1)} disabled={!canContinue}>
            {t('deck.wizard.next')}
          </Button>
        ) : (
          <Button variant="brand" size="brand" onClick={submit} disabled={!canContinue || isSubmitting}>
            {t('deck.wizard.submit')}
          </Button>
        )}
      </div>
    </div>
  );
}
